import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { addToast } from "components/notifications";
import { useClientQueryWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { quoteStatus } from "constants/quoteStatus";
import { QUOTE_WITH_ASSETS_QUERY } from "features/quote/queries";
import { LIST_QUOTE_ITEM_QUERY } from "features/quotes/queries";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { APPLY_LAST_PRICE_CARD_VERSION, VALIDATE_PRICE_CARD } from "../mutations";
import { emptyArray } from "constants/defaultValues";
import { get, set, useStateLens } from "@totalsoft/react-state-lens";
import { useApolloClient } from "@apollo/client";
import Fragments from "features/quotes/fragments";
import { invalidateApolloClientCache } from "utils/functions";

function QuoteRouteRedirect(props) {
  const { DefaultComponent } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const { quoteId } = useParams();
  const client = useApolloClient();
  const clientQuery = useClientQueryWithErrorHandling();
  const localStateLens = useStateLens({ validationStatus: "PENDING", component: DefaultComponent });
  const Component = localStateLens.component |> get;

  const listQuoteItemCacheId = client.cache.identify({ __typename: "ListQuoteItem", quoteId: parseInt(quoteId) });
  const listQuoteItemFromCache = client.cache.readFragment({
    id: listQuoteItemCacheId,
    fragment: Fragments.listQuoteItem,
  });

  const { loading: loadingListQuoteItem, data } = useQueryWithErrorHandling(LIST_QUOTE_ITEM_QUERY, {
    skip: Boolean(listQuoteItemFromCache),
    variables: { quoteId: parseInt(quoteId) },
  });
  const listQuoteItem = listQuoteItemFromCache || data?.listQuoteItem;

  const [validatePriceCard] = useMutationWithErrorHandling(VALIDATE_PRICE_CARD);
  const [applyLatestPriceCardVersion] = useMutationWithErrorHandling(APPLY_LAST_PRICE_CARD_VERSION);

  useEffect(() => {
    (async () => {
      if (loadingListQuoteItem || (localStateLens.loading |> get) === "DONE") {
        return;
      }

      if (!listQuoteItem?.quote?.quoteId) {
        history.push({ pathname: `/overview`, state: { quoteId } });
        return;
      }

      const { quoteStatusId } = listQuoteItem.quote;
      if (quoteStatusId === quoteStatus.noFinanceProduct.id) {
        const { data } = await validatePriceCard({ variables: { quoteId: parseInt(quoteId) } });
        if (data?.validatePriceCard) {
          addToast(t("DialogMessages.PriceCardHasNewVersion"), "warning");
          const { data } = await applyLatestPriceCardVersion({ variables: { quoteId: parseInt(quoteId) } });
          if (!data?.applyLatestPriceCardVersion) {
            const errorMessage = t("DialogMessages.ApplyLatestPriceCardVersionError");
            history.push({ pathname: `/overview`, state: { errorMessage } });
            return;
          } else {
            invalidateApolloClientCache(client, { __typename: "Quote", quoteId: parseInt(quoteId) }, ["quoteProducts"]);
          }
        }

        if (
          history.location.pathname !== `/quotes/${quoteId}/assets` &&
          history.location.pathname !== `/quotes/${quoteId}/client`
        ) {
          const { data: quoteWithAssetsData } = await clientQuery(QUOTE_WITH_ASSETS_QUERY, {
            variables: { quoteId: parseInt(quoteId) },
          });
          const quoteAssets = quoteWithAssetsData?.quote?.quoteAssets || emptyArray;

          if (quoteAssets.length === 0) {
            const errorMessage = t("DialogMessages.NotAssets");
            history.push({ pathname: `/quotes/${quoteId}/assets`, state: { errorMessage } });
            return;
          }

          if (quoteAssets.some((a) => !a.assetNetPrice && !a.assetNetPriceWithVAT)) {
            const errorMessage = t("DialogMessages.AssetsInvalidValues");
            history.push({ pathname: `/quotes/${quoteId}/assets`, state: { errorMessage } });
            return;
          }
        }

        if (
          history.location.pathname === `/quotes/${quoteId}/datacompletion` ||
          history.location.pathname === `/quotes/${quoteId}/contract`
        ) {
          history.push({ pathname: `/quotes/${quoteId}/products` });
          return;
        }
      }

      if (
        history.location.pathname === `/quotes/${quoteId}/contract` &&
        (quoteStatusId === quoteStatus.incompleteCustomerData.id ||
          quoteStatusId === quoteStatus.pendingCustomerInfo.id)
      ) {
        history.push({ pathname: `/quotes/${quoteId}/datacompletion` });
        return;
      }

      if (
        history.location.pathname === `/quotes/${quoteId}/client` &&
        quoteStatusId !== quoteStatus.noFinanceProduct.id
      ) {
        if (
          quoteStatusId === quoteStatus.incompleteCustomerData.id ||
          quoteStatusId === quoteStatus.pendingCustomerInfo.id
        ) {
          history.push({ pathname: `/quotes/${quoteId}/datacompletion` });
        }
        if (
          quoteStatusId === quoteStatus.pendingDecision.id ||
          quoteStatusId === quoteStatus.declined.id ||
          quoteStatusId === quoteStatus.pendingDocumentsUpload.id ||
          quoteStatusId === quoteStatus.pendingDocumentsFromCustomer.id ||
          quoteStatusId === quoteStatus.completed.id ||
          quoteStatusId === quoteStatus.funded.id ||
          quoteStatusId === quoteStatus.cancelled.id ||
          quoteStatusId === quoteStatus.documentsUploaded.id ||
          quoteStatusId === quoteStatus.approved.id ||
          quoteStatusId === quoteStatus.approvedWithConditions.id
        ) {
          history.push({ pathname: `/quotes/${quoteId}/contract` });
        }
        return;
      }

      (localStateLens.loading |> set)("DONE");
    })();
  }, [
    listQuoteItem,
    loadingListQuoteItem,
    quoteId,
    localStateLens.component,
    localStateLens.componentState,
    localStateLens.loading,
    history,
    client,
    t,
    applyLatestPriceCardVersion,
    clientQuery,
    validatePriceCard,
  ]);

  const loading = (localStateLens.loading |> get) !== "DONE" || loadingListQuoteItem;
  if (loading) {
    return <LoadingFakeText lines={10} />;
  }

  return <Component />;
}

QuoteRouteRedirect.propTypes = {
  DefaultComponent: PropTypes.func,
};

export default QuoteRouteRedirect;
